import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Thermometer, MapPin, AlertTriangle, Activity } from "lucide-react";

export default function ColdChainLog({ orderId, status }) {
  const [data, setData] = useState(null);
  const timer = useRef(null);
  const active = status === "dispatched";

  const load = async () => {
    try {
      const { data } = await api.get(`/orders/${orderId}/cold-chain`);
      setData(data);
    } catch (_) {}
  };

  useEffect(() => {
    load();
    if (active) {
      timer.current = setInterval(load, 15000);
      return () => clearInterval(timer.current);
    }
    // eslint-disable-next-line
  }, [orderId, active]);

  if (!data || !data.logs || data.logs.length === 0) {
    return (
      <div className="surface-card p-6" data-testid="coldchain-empty">
        <h3 className="heading font-semibold mb-2 flex items-center gap-2"><Thermometer className="h-4 w-4" /> Cold-chain log</h3>
        <p className="text-sm text-stone-500">Telemetry starts when the bio-courier dispatches this order.</p>
      </div>
    );
  }

  const { latest, safe_range, alarm_count } = data;
  const logs = data.logs.slice(-40);
  const temps = logs.map(l => l.temperature_c);
  const minT = Math.min(...temps, safe_range.min);
  const maxT = Math.max(...temps, safe_range.max);
  const span = maxT - minT || 1;

  return (
    <div className="surface-card p-6" data-testid="coldchain-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h3 className="heading font-semibold flex items-center gap-2">
          <Thermometer className="h-4 w-4" /> Cold-chain log
          {active && <span data-testid="coldchain-live-badge" className="text-[10px] mono uppercase px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 flex items-center gap-1"><Activity className="h-3 w-3" /> Live · 15s</span>}
        </h3>
        {alarm_count > 0 && (
          <span data-testid="coldchain-alarm-badge" className="text-xs mono px-2 py-1 rounded-full pill-critical inline-flex items-center gap-1">
            <AlertTriangle className="h-3.5 w-3.5" /> {alarm_count} excursion{alarm_count > 1 ? "s" : ""}
          </span>
        )}
      </div>

      <div className="mt-4 grid sm:grid-cols-3 gap-3">
        <div className="border border-stone-200 rounded-lg p-4 bg-[color:var(--bio-frost-bg,#F0F9FF)]">
          <div className="text-[10px] uppercase tracking-widest text-stone-400">Current temp</div>
          <div data-testid="coldchain-current-temp" className="heading text-2xl font-extrabold mono text-[#0284C7]">{latest.temperature_c}°C</div>
          <div className="text-xs text-stone-500 mono mt-1">safe {safe_range.min}°C … {safe_range.max}°C</div>
        </div>
        <div className="border border-stone-200 rounded-lg p-4">
          <div className="text-[10px] uppercase tracking-widest text-stone-400">GPS position</div>
          <div data-testid="coldchain-gps" className="mono text-sm mt-1 flex items-center gap-1"><MapPin className="h-4 w-4 text-stone-400" /> {latest.lat}, {latest.lng}</div>
          <div className="text-xs text-stone-500 mono mt-1">tilt {latest.tilt_deg}°</div>
        </div>
        <div className="border border-stone-200 rounded-lg p-4">
          <div className="text-[10px] uppercase tracking-widest text-stone-400">Readings</div>
          <div className="heading text-2xl font-extrabold mono">{data.logs.length}</div>
          <div className="text-xs text-stone-500 mono mt-1">last ping {new Date(latest.recorded_at).toLocaleTimeString()}</div>
        </div>
      </div>

      {/* Temperature sparkline */}
      <div className="mt-5">
        <div className="text-[10px] uppercase tracking-widest text-stone-400 mb-2">Temperature trace (°C)</div>
        <div data-testid="coldchain-sparkline" className="flex items-end gap-[2px] h-20 border border-stone-200 rounded-md p-1 bg-stone-50/50">
          {logs.map((l, i) => {
            const h = Math.max(8, Math.round(((l.temperature_c - minT) / span) * 90));
            const bad = l.temperature_c > safe_range.max;
            return <div key={i} title={`${l.temperature_c}°C`} style={{ height: `${h}%` }}
                        className={`flex-1 rounded-sm ${bad ? "bg-red-400" : "bg-[#0284C7]"}`} />;
          })}
        </div>
        <div className="flex justify-between text-[10px] mono text-stone-400 mt-1">
          <span>{maxT.toFixed(1)}°C</span><span>{minT.toFixed(1)}°C</span>
        </div>
      </div>

      {/* GPS route trail */}
      <div className="mt-5">
        <div className="text-[10px] uppercase tracking-widest text-stone-400 mb-2">GPS trail</div>
        <svg data-testid="coldchain-gps-trail" viewBox="0 0 400 120" className="w-full h-28 border border-stone-200 rounded-md bg-stone-50/50">
          {(() => {
            const lats = logs.map(l => l.lat), lngs = logs.map(l => l.lng);
            const mLat = Math.min(...lats), MLat = Math.max(...lats);
            const mLng = Math.min(...lngs), MLng = Math.max(...lngs);
            const X = (lng) => 20 + ((lng - mLng) / ((MLng - mLng) || 1)) * 360;
            const Y = (lat) => 100 - ((lat - mLat) / ((MLat - mLat) || 1)) * 80;
            const pts = logs.map(l => `${X(l.lng)},${Y(l.lat)}`).join(" ");
            const first = logs[0], last = logs[logs.length - 1];
            return (
              <>
                <polyline points={pts} fill="none" stroke="#16382B" strokeWidth="1.5" strokeDasharray="4 3" />
                <circle cx={X(first.lng)} cy={Y(first.lat)} r="4" fill="#16A34A" />
                <circle cx={X(last.lng)} cy={Y(last.lat)} r="4" fill="#DC2626" />
              </>
            );
          })()}
        </svg>
        <div className="flex justify-between text-[10px] mono text-stone-400 mt-1"><span>origin</span><span>current</span></div>
      </div>

      {/* Recent readings table */}
      <div className="mt-5 overflow-auto max-h-56">
        <table className="data-table">
          <thead><tr><th>Time</th><th>Temp</th><th>GPS</th><th>Tilt</th><th>Alarm</th></tr></thead>
          <tbody>
            {[...data.logs].reverse().slice(0, 12).map(l => (
              <tr key={l.id}>
                <td className="mono text-xs">{new Date(l.recorded_at).toLocaleTimeString()}</td>
                <td className="mono">{l.temperature_c}°C</td>
                <td className="mono text-xs">{l.lat}, {l.lng}</td>
                <td className="mono text-xs">{l.tilt_deg}°</td>
                <td>{l.alarm ? <span className="text-xs mono px-2 py-0.5 rounded-full pill-critical">{l.alarm}</span> : <span className="text-stone-300">—</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
